// src/WarrantySalesAdminPage.js
import React, { useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

async function apiFetch(path, { method = "GET", body, headers } = {}) {
  const base = process.env.REACT_APP_API_BASE;
  if (!base) throw new Error("Missing REACT_APP_API_BASE in .env");

  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error("Missing ID token (please sign out/in again)");

  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(msg.includes("HTTP") ? msg : `${msg} (HTTP ${res.status})`);
  }
  return data;
}

function qsValue(name) {
  const p = new URLSearchParams(window.location.search);
  return p.get(name) || "";
}

// Convert YYYY-MM-DD -> DD/MM/YYYY (if empty or invalid, return "")
function isoToDmy(iso) {
  const s = (iso || "").trim();
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export default function WarrantySalesAdminPage() {
  const wrId = qsValue("wrId").trim();

  const qsOrange = qsValue("orange").trim();
  const qsPurple = qsValue("purple").trim();
  const orangeEnabled = qsOrange ? qsOrange !== "0" : true;
  const purpleEnabled = qsPurple ? qsPurple !== "0" : true;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // Store dates in ISO format (YYYY-MM-DD) for <input type="date">
  const [orange, setOrange] = useState({
    sapReturnsOrderNo: "",
    sapReturnsDeliveryOrderNo: "",
    sapReturnsCreationDate: "", // ISO: YYYY-MM-DD
  });

  const [purple, setPurple] = useState({
    salesOrderNo: "",
    soEntryDate: "", // ISO: YYYY-MM-DD
  });

  function buildSalesAdminPayload() {
    return {
      salesAdminData: {
        orange: orangeEnabled
          ? {
              sapReturnsOrderNo: (orange.sapReturnsOrderNo || "").trim(),
              sapReturnsDeliveryOrderNo: (orange.sapReturnsDeliveryOrderNo || "").trim(),
              // Convert to DD/MM/YYYY for backend/excel consistency
              sapReturnsCreationDate: isoToDmy(orange.sapReturnsCreationDate),
            }
          : {},
        purple: purpleEnabled
          ? {
              salesOrderNo: (purple.salesOrderNo || "").trim(),
              // Convert to DD/MM/YYYY for backend/excel consistency
              soEntryDate: isoToDmy(purple.soEntryDate),
            }
          : {},
      },
    };
  }

  async function submit() {
    if (!wrId) {
      setError("Missing wrId in URL. Example: #/warranty_salesadmin?wrId=WR-xxxxx");
      return;
    }

    const ok = window.confirm("Submit and email Logistics.\n\nContinue?");
    if (!ok) return;

    setBusy(true);
    setError("");
    setOkMsg("");

    try {
      await apiFetch(`/warranty/${encodeURIComponent(wrId)}/sales-admin`, {
        method: "PUT",
        body: buildSalesAdminPayload(),
      });

      const resp = await apiFetch(`/warranty/${encodeURIComponent(wrId)}/sales-admin/submit`, {
        method: "POST",
        body: {},
      });

      const note = resp?.note ? ` ${resp.note}` : "";
      setOkMsg("Submitted. Files are being generated and Logistics will be notified by email.");
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
      <div style={{ fontSize: 18, fontWeight: 900 }}>Warranty (Sales Admin 1)</div>

      <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
        wrId: <span style={mono}>{wrId || "-"}</span>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
        Please fill in the information below and click Submit.
      </div>

      {error ? <div style={{ marginTop: 12, color: "#b91c1c", fontWeight: 800 }}>{error}</div> : null}
      {okMsg ? <div style={{ marginTop: 12, color: "#166534", fontWeight: 800 }}>{okMsg}</div> : null}

      {orangeEnabled ? (
        <div
  style={{
    marginTop: 14,
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    borderRadius: 12,
    padding: 12,
  }}
>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>Form for Returned Goods</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800 }}>SAP Returns Order No.</div>
              <input
                value={orange.sapReturnsOrderNo}
                disabled={busy}
                onChange={(e) => setOrange((p) => ({ ...p, sapReturnsOrderNo: e.target.value }))}
                style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #e2e8f0" }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 800 }}>SAP Returns Delivery Order No.</div>
              <input
                value={orange.sapReturnsDeliveryOrderNo}
                disabled={busy}
                onChange={(e) => setOrange((p) => ({ ...p, sapReturnsDeliveryOrderNo: e.target.value }))}
                style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #e2e8f0" }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 800 }}>SAP Returns Creation Date</div>
              <input
                type="date"
                value={orange.sapReturnsCreationDate}
                disabled={busy}
                onChange={(e) => setOrange((p) => ({ ...p, sapReturnsCreationDate: e.target.value }))}
                style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #e2e8f0" }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {purpleEnabled ? (
        <div
  style={{
    marginTop: 14,
    border: "1px solid #e9d5ff",
    background: "#faf5ff",
    borderRadius: 12,
    padding: 12,
  }}
>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>Request for Delivery</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800 }}>Sales Order No.</div>
              <input
                value={purple.salesOrderNo}
                disabled={busy}
                onChange={(e) => setPurple((p) => ({ ...p, salesOrderNo: e.target.value }))}
                style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #e2e8f0" }}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 800 }}>S/O Entry Date</div>
              <input
                type="date"
                value={purple.soEntryDate}
                disabled={busy}
                onChange={(e) => setPurple((p) => ({ ...p, soEntryDate: e.target.value }))}
                style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #e2e8f0" }}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          onClick={submit}
          disabled={busy || !wrId}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
            fontWeight: 900,
            background: "#0f172a",
            color: "white",
            opacity: busy || !wrId ? 0.7 : 1,
            cursor: busy || !wrId ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "Submitting..." : "Submit"}
        </button>
      </div>
    </div>
  );
}